import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import swal from 'sweetalert';
import TeamService from '../../../services/TeamService';
import {
  PageHeader, Card, Button, Dot, Table, Tr, Td, EmptyState, Field, Input, Toggle,
} from '../../ui';

const TeamManagement = () => {
  const nav = useNavigate();
  const [access, setAccess] = useState(null); // { allowed, used, max }
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [adding, setAdding] = useState(false);

  // One-time credential reveal (add or reset)
  const [reveal, setReveal] = useState(null); // { name, email, password }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const a = await TeamService.access();
      setAccess(a);
      if (a.allowed) setMembers(await TeamService.list());
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const copy = (text, label) =>
    navigator.clipboard.writeText(text).then(() =>
      swal('Copied!', `${label} copied to clipboard.`, 'success', { timer: 1200, buttons: false }));

  const addMember = async () => {
    if (!name.trim() || !email.trim()) return;
    setAdding(true);
    try {
      const r = await TeamService.add(name.trim(), email.trim());
      setShowAdd(false);
      setName(''); setEmail('');
      setReveal({ name: r.data.name, email: r.data.email, password: r.temp_password });
      load();
    } catch (e) {
      swal('Error', e.response?.data?.error || 'Failed to add team member', 'error');
    } finally { setAdding(false); }
  };

  const toggleActive = async (m) => {
    await TeamService.update(m.id, { is_active: !m.is_active });
    load();
  };

  const rename = async (m) => {
    const newName = await swal({
      title: 'Rename member', text: 'Display name',
      content: { element: 'input', attributes: { value: m.name } },
      buttons: ['Cancel', 'Save'],
    });
    if (!newName) return;
    await TeamService.update(m.id, { name: String(newName).trim() });
    load();
  };

  const resetPassword = async (m) => {
    const ok = await swal({
      title: 'Reset password?', text: `Generate a new temporary password for ${m.name}. Their old password stops working.`,
      icon: 'warning', buttons: ['Cancel', 'Reset'],
    });
    if (!ok) return;
    const password = await TeamService.resetPassword(m.id);
    setReveal({ name: m.name, email: m.email, password });
  };

  const removeMember = async (m) => {
    const ok = await swal({
      title: 'Remove team member?',
      text: `${m.name} and their stream will be permanently deleted. This cannot be undone.`,
      icon: 'warning', buttons: ['Cancel', 'Remove'], dangerMode: true,
    });
    if (!ok) return;
    await TeamService.remove(m.id);
    load();
  };

  return (
    <>
      <PageHeader
        title="Team"
        subtitle="Add camera operators who broadcast from the mobile app — their streams show up as inputs in your Live Switcher"
        actions={access?.allowed && (
          <Button
            icon="fa-user-plus"
            onClick={() => setShowAdd(true)}
            disabled={access.max > 0 && access.used >= access.max}
          >
            Add member
          </Button>
        )}
      />

      {loading ? (
        <div className="tw-py-16 tw-text-center"><i className="fa-solid fa-spinner fa-spin tw-text-2xl tw-text-brand" /></div>
      ) : !access?.allowed ? (
        <Card>
          <EmptyState
            icon="fa-lock"
            title="Team management isn't in your plan"
            hint="Upgrade to a plan that includes Team Management to add camera operators who broadcast from the mobile app."
            action={<Button icon="fa-tags" onClick={() => nav('/pricing')}>View plans</Button>}
          />
        </Card>
      ) : (
        <>
          {access.max > 0 && (
            <div className="tw-mb-4 tw-text-xs tw-font-semibold tw-text-content-muted">
              {access.used} / {access.max} team member{access.max === 1 ? '' : 's'} used
            </div>
          )}
          <Card noPad>
            {members.length === 0 ? (
              <EmptyState
                icon="fa-users"
                title="No team members yet"
                hint="Add an operator — we'll create mobile-app credentials and a dedicated stream for them."
                action={<Button icon="fa-user-plus" onClick={() => setShowAdd(true)}>Add member</Button>}
              />
            ) : (
              <Table head={['Member', 'Stream', 'Status', 'Last active', '']}>
                {members.map((m) => (
                  <Tr key={m.id}>
                    <Td>
                      <div className="tw-min-w-0">
                        <div className="tw-truncate tw-font-semibold tw-text-heading">{m.name}</div>
                        <div className="tw-truncate tw-text-xs tw-text-content-faint">{m.email}</div>
                      </div>
                    </Td>
                    <Td>
                      {m.stream ? (
                        <div className="tw-flex tw-items-center tw-gap-2">
                          {m.stream.is_live && <Dot tone="live" pulse />}
                          <span className="tw-truncate">{m.stream.name}</span>
                        </div>
                      ) : <span className="tw-text-content-faint">—</span>}
                    </Td>
                    <Td>
                      <Toggle checked={!!m.is_active} onChange={() => toggleActive(m)} />
                      <span className="tw-ml-2 tw-text-xs tw-text-content-muted">{m.is_active ? 'Active' : 'Suspended'}</span>
                    </Td>
                    <Td className="tw-text-content-muted">
                      {m.last_login_at ? new Date(m.last_login_at).toLocaleDateString() : 'Never logged in'}
                    </Td>
                    <Td className="tw-text-right">
                      <div className="tw-flex tw-justify-end tw-gap-1">
                        <Button variant="ghost" size="sm" icon="fa-pen" title="Rename" onClick={() => rename(m)} />
                        <Button variant="ghost" size="sm" icon="fa-key" title="Reset password" onClick={() => resetPassword(m)} />
                        <Button variant="ghost" size="sm" icon="fa-trash" title="Remove" onClick={() => removeMember(m)} />
                      </div>
                    </Td>
                  </Tr>
                ))}
              </Table>
            )}
          </Card>
        </>
      )}

      {/* Add member */}
      <Modal show={showAdd} onHide={() => setShowAdd(false)} centered>
        <Modal.Header closeButton><Modal.Title>Add team member</Modal.Title></Modal.Header>
        <Modal.Body>
          <Field label="Name">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Alex Rivera" autoFocus />
          </Field>
          <div className="tw-mt-3">
            <Field label="Email">
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="alex@example.com" />
            </Field>
          </div>
          <p className="tw-mt-3 tw-text-xs tw-text-content-muted">
            We'll create mobile-app sign-in credentials and one dedicated stream for them.
            A one-time temporary password is shown after you add them.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAdd(false)}>Cancel</Button>
          <Button variant="primary" icon="fa-user-plus" onClick={addMember} disabled={adding || !name.trim() || !email.trim()}>
            {adding ? 'Adding…' : 'Add member'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* One-time credential reveal */}
      <Modal show={!!reveal} onHide={() => setReveal(null)} centered>
        <Modal.Header closeButton><Modal.Title>Mobile app credentials</Modal.Title></Modal.Header>
        <Modal.Body>
          <p className="tw-text-sm tw-text-content-muted">
            Share these with <strong>{reveal?.name}</strong> — the password is shown <strong>once</strong>.
            They'll sign in from the Teve mobile app and can change it after.
          </p>
          <div className="tw-mt-3 tw-space-y-2">
            <div className="tw-flex tw-items-center tw-justify-between tw-rounded-lg tw-bg-ink-800 tw-px-3 tw-py-2">
              <div>
                <div className="tw-text-[11px] tw-uppercase tw-text-content-faint">Email</div>
                <code className="tw-text-sm tw-text-content">{reveal?.email}</code>
              </div>
              <button className="tw-text-content-faint hover:tw-text-heading" onClick={() => copy(reveal?.email, 'Email')}>
                <i className="fa-solid fa-copy" />
              </button>
            </div>
            <div className="tw-flex tw-items-center tw-justify-between tw-rounded-lg tw-bg-ink-800 tw-px-3 tw-py-2">
              <div>
                <div className="tw-text-[11px] tw-uppercase tw-text-content-faint">Temporary password</div>
                <code className="tw-text-sm tw-text-content">{reveal?.password}</code>
              </div>
              <button className="tw-text-content-faint hover:tw-text-heading" onClick={() => copy(reveal?.password, 'Password')}>
                <i className="fa-solid fa-copy" />
              </button>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setReveal(null)}>Done</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TeamManagement;
